import {
  type Cell,
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Header,
  type PaginationState,
  type Row,
  type RowData,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import {
  type AriaAttributes,
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import useResizeObserver from 'use-resize-observer';
import { Button } from '../Button/Button';
import { Pagination } from '../Pagination/Pagination';
import SortingArrows from '../SortingArrows';
import { Tooltip } from '../Tooltip/Tooltip';
import { ColumnWithFilter } from './private/ColumnWithFilter';

// NOTE: should be remove once the issue is fixed in tanstack/react-table
/** workaround for unexpected behavior with tanstack/react-table arrIncludeSome
 * @see https://github.com/TanStack/table/issues/5617  */
export function arrayFilterSomeStrictContains<T>(
  row: Row<T>,
  columnId: string,
  filterValue: unknown[],
) {
  return (
    filterValue.length === 0 ||
    filterValue.some((value: string) => value === row.original[columnId])
  );
}

declare module '@tanstack/table-core' {
  export interface ColumnMeta<TData extends RowData, TValue> {
    /** Specifies whether the contents in the column should be right-aligned. */
    rightAlign?: boolean;
    /** Text to display as a tooltip for the column. */
    tooltip?: string;
    /** Defines the filter variant based on the customer filter function; if a custom filter function is specified, the variant is determined based on it; otherwise, it is inferred from the default filter functions. */
    filterVariant?: 'select';
    /**
     * Enables searching of filter options.
     * Default value is true.
     * @type {boolean}
     */
    showSearchInFilter?: boolean;

    filterOptionSortFn?: (a: TValue, b: TValue) => number;
    /**
     * Accessor function to extract a specific value from the filter option for display.
     * In case the cell value is a complex object, this function can be used to extract a specific value for display and filtering.
     * @param {TValue} value - The value of the filter option.
     * @returns {string | number | undefined | null} The extracted value for display.
     */
    filterOptionValueAccessor?: (
      value: TValue,
    ) => string | number | undefined | null;
    /**
     * Custom text label for filter options that are searchable when 'showSearchInFilter' is enabled.
     * @param {TValue} value - The value of the filter option.
     * @param {Column<TData, TValue>} column - The column of the table.
     * @returns {string | null | undefined} The custom label for the filter option.
     */
    filterOptionLabel?: (
      value: TValue,
      column: Column<TData, TValue>,
    ) => string | null | undefined;
    /**
     * Custom render function for displaying filter options in the filter checkbox list.
     * Utilizes the 'filterOptionLabel' function if provided.
     * @param {TValue} value - The value of the filter option.
     * @param {Column<TData, TValue>} column - The column of the table.
     * @returns {React.ReactNode} The React node representing the custom filter option.
     */
    renderFilterOption?: (
      value: TValue,
      column: Column<TData, TValue>,
    ) => React.ReactNode;
    /**
     * Specifies whether the column should be hidden in the table.
     */
    hideSorting?: boolean;
    /**
     * Custom title for the filter.
     * @type {string}
     */
    filterTitleOverride?: string;
    /**
     * Custom placeholder for the filter.
     * @type {string}
     */
    filterPlaceholder?: string;
  }
}

export type TableProps<TData, TDisplayKeys extends string = never> = {
  className?: string;
  columns: ColumnDef<TData>[];
  data: TData[];
  size?: 'default' | 'relaxed' | 'compact';
  fixed?: boolean;
  paginated?: boolean;
  stickyHeader?: boolean;
  pinning?: {
    left: Array<keyof TData | TDisplayKeys>;
    right: Array<keyof TData | TDisplayKeys>;
  };
  pageSize?: number;
  pageCount?: number;
  sortable?: boolean;
  sortDescFirst?: boolean;
  filterable?: boolean;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  filters?: ColumnFiltersState;
  manualPagination?: boolean;
  getRowClassName?: (row: Row<TData>) => string;
  getCellClassName?: (cell: Cell<TData, unknown>) => string;
  getHeaderClassName?: (cell: Header<TData, unknown>) => string;
  caption?: string;
  onRowClick?: (row: Row<TData>) => void;
  onRowMouseEnter?: (row: Row<TData>) => void;
  onRowMouseLeave?: (row: Row<TData>) => void;
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  initialSorting?: SortingState;
};

function getCommonPinningStyles<T>(column: Column<T>): CSSProperties {
  const isPinned = column.getIsPinned();
  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
}

export function Table<TData, TDisplayKeys extends string = never>(
  props: TableProps<TData, TDisplayKeys>,
) {
  const {
    columns,
    data,
    size = 'default',
    fixed = false,
    getRowClassName,
    getCellClassName,
    getHeaderClassName,
    stickyHeader = false,
    sortable,
    sortDescFirst = false,
    filterable,
    onFilterChange,
    filters,
    paginated,
    pinning,
    className,
    onRowClick,
    onRowMouseEnter,
    onRowMouseLeave,
    columnVisibility,
    columnOrder,
    manualPagination = false,
  } = props;
  const { t } = useTranslation('ui');
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const handleTableScrollPosition = useCallback((): void => {
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(() => {
      const target = tableContainerRef.current as HTMLDivElement | undefined;
      if (!target) return;
      const scrollWidth = target.scrollWidth - target.clientWidth;
      const isScrolled = target.scrollLeft > 0;
      const isScrolledToEnd = Math.round(target.scrollLeft) >= scrollWidth;

      target.dataset.scrolled = isScrolled ? 'true' : 'false';
      target.dataset.scrolledToEnd = isScrolledToEnd ? 'true' : 'false';
      rafRef.current = null;
    });
  }, []);

  useResizeObserver({
    ref: tableContainerRef.current,
    onResize: handleTableScrollPosition,
  });

  useEffect(() => {
    const container = tableContainerRef.current as HTMLDivElement;
    container.addEventListener('scroll', handleTableScrollPosition, {
      passive: true,
    });

    return () => {
      container.removeEventListener('scroll', handleTableScrollPosition);
    };
  }, [handleTableScrollPosition]);

  const [sorting, setSorting] = useState<SortingState>(
    props.initialSorting ?? [],
  );

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    (pinning as ColumnPinningState | undefined) || { left: [], right: [] },
  );
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);

  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      if (typeof onFilterChange === 'function') {
        onFilterChange(filters);
      } else {
        setInternalColumnFilters(filters);
      }
    },
    [onFilterChange],
  );

  const columnFilters = filters ?? internalColumnFilters;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: props.pageSize ?? 12,
  });

  const pagination = useMemo(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  const table = useReactTable<TData>({
    columns,
    data,
    state: {
      sorting: sorting.length > 0 ? sorting : props.initialSorting,
      pagination,
      columnPinning,
      columnFilters,
      columnVisibility,
      columnOrder,
    },
    getCoreRowModel: getCoreRowModel(),
    ...(filterable
      ? {
          getFilteredRowModel: getFilteredRowModel(),
          onColumnFiltersChange: handleFilterChange,
        }
      : {
          enableColumnFilters: false,
        }),
    ...(pinning
      ? { onColumnPinningChange: setColumnPinning }
      : {
          enableColumnPinning: false,
        }),
    ...(sortable
      ? {
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
          sortDescFirst, // Defaults to false (ascending first), can be overridden via props
        }
      : {}),
    ...(paginated
      ? {
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          ...(manualPagination
            ? {
                manualPagination,
                pageCount: props.pageCount,
              }
            : {}),
        }
      : {}),
  });

  const footerIncludesData = table.getFooterGroups().some((groups) => {
    return groups.headers.some((group) => group.column.columnDef.footer);
  });

  const paginatedRows = table.getPaginationRowModel().rows;
  const prePaginatedRows = table.getPrePaginationRowModel().rows;

  const { fromItemIndex, toItemIndex } = useMemo(() => {
    if (prePaginatedRows.length === 0) return {};
    if (!paginated)
      return { fromItemIndex: 1, toItemIndex: prePaginatedRows.length };

    return {
      fromItemIndex: pageSize * pageIndex + 1,
      toItemIndex:
        paginatedRows.length - 1 >= pageSize
          ? prePaginatedRows.length
          : pageSize * pageIndex + 1 + (paginatedRows.length - 1),
    };
  }, [paginatedRows, paginated, pageSize, prePaginatedRows, pageIndex]);

  return (
    <>
      <div
        className={twMerge(
          'relative overflow-auto rounded',
          'laane:border-neutral-10 laane:rounded-md laane:border',
          className,
        )}
        ref={tableContainerRef}
        data-scrolled="false"
        data-scrolled-to-end="false"
      >
        <table
          className={twMerge(
            'w-full bg-inherit',
            clsx(
              {
                'table-relaxed': size === 'relaxed',
                'table-compact': size === 'compact',
              },
              fixed ? 'table-fixed' : 'table-auto',
            ),
          )}
        >
          <thead
            data-is-sticky={stickyHeader ? 'true' : undefined}
            className="group/header"
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="group/row">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    data-testid={`header-${header.column.id}`}
                    role="columnheader"
                    data-is-pinned={
                      header.column.getIsPinned() ? 'true' : undefined
                    }
                    data-is-last-left-pinned-column={
                      header.column.getIsPinned() === 'left' &&
                      header.column.getIsLastColumn('left')
                        ? 'true'
                        : undefined
                    }
                    data-is-first-right-pinned-column={
                      header.column.getIsPinned() === 'right' &&
                      header.column.getIsFirstColumn('right')
                        ? 'true'
                        : undefined
                    }
                    style={{
                      width: `${header.getSize()}px`,
                      ...getCommonPinningStyles(header.column),
                      position: 'sticky',
                      top: 0,
                    }}
                    className={getHeaderClassName?.(header)}
                    {...(sortable && header.column.getCanSort()
                      ? {
                          'aria-sort': match(header.column.getIsSorted())
                            .returnType<AriaAttributes['aria-sort']>()
                            .with('asc', () => 'ascending')
                            .with('desc', () => 'descending')
                            .otherwise(() => 'none'),
                        }
                      : {})}
                  >
                    <Tooltip
                      enabled={!!header.column.columnDef.meta?.tooltip}
                      tooltip={header.column.columnDef.meta?.tooltip}
                    >
                      <div
                        className={clsx(
                          'flex items-end',
                          {
                            'justify-end':
                              header.column.columnDef.meta?.rightAlign,
                          },
                          sortable &&
                            header.column.getCanSort() &&
                            filterable &&
                            header.column.getCanFilter()
                            ? 'laane:space-x-0'
                            : 'space-x-2',
                        )}
                      >
                        {filterable && header.column.getCanFilter() ? (
                          <ColumnWithFilter<TData>
                            data={data}
                            table={table}
                            column={header.column}
                            label={
                              header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )
                            }
                          />
                        ) : (
                          <div className="kunde:mt-2 mt-1 text-nowrap kunde:py-1.5">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </div>
                        )}
                        {sortable &&
                        header.column.getCanSort() &&
                        header.column.columnDef.meta?.hideSorting !== true ? (
                          <Button
                            compact
                            data-testid={`sort-${header.column.id}`}
                            theme="subtle"
                            onClick={header.column.getToggleSortingHandler()}
                            className={clsx(
                              'text-neutral-40 hover:text-neutral-60 focus:text-neutral-60 translate-y-1 cursor-pointer self-center p-1.5',
                              'kunde:bg-transparent kunde:text-neutral-60 kunde:hover:bg-blue-10 kunde:hover:text-neutral-100 sm:kunde:-ml-1 md:kunde:p-1.5',
                            )}
                            title={
                              header.column.getCanSort()
                                ? header.column.getNextSortingOrder() === 'asc'
                                  ? t('table.sort.asc')
                                  : header.column.getNextSortingOrder() ===
                                      'desc'
                                    ? t('table.sort.desc')
                                    : t('table.sort.none')
                                : undefined
                            }
                          >
                            <SortingArrows
                              sorting={header.column.getIsSorted()}
                            />
                          </Button>
                        ) : null}
                      </div>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-inherit">
            {table.getRowModel().rows.map((row: Row<TData>) => (
              <tr
                onClick={() => {
                  if (onRowClick) {
                    onRowClick(row);
                  }
                }}
                onMouseEnter={() => {
                  if (onRowMouseEnter) {
                    onRowMouseEnter(row);
                  }
                }}
                onMouseLeave={() => {
                  if (onRowMouseLeave) {
                    onRowMouseLeave(row);
                  }
                }}
                key={row.id}
                role="row"
                className={twMerge(
                  clsx('group/row content-visibility-auto bg-inherit'),
                  getRowClassName?.(row),
                )}
              >
                {row.getVisibleCells().map((cell, index) => (
                  <td
                    // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
                    role="cell"
                    data-value={JSON.stringify(cell.getValue())}
                    key={cell.id}
                    data-testid={`cell-${cell.column.id}`}
                    data-column-id={cell.column.id}
                    className={twMerge(
                      clsx('group/cell bg-inherit', {
                        'text-right': cell.column.columnDef.meta?.rightAlign,
                        'peer/first-col': index === 0,
                      }),
                      getCellClassName?.(cell),
                    )}
                    data-is-pinned={
                      cell.column.getIsPinned() ? 'true' : undefined
                    }
                    data-is-last-left-pinned-column={
                      cell.column.getIsPinned() === 'left' &&
                      cell.column.getIsLastColumn('left')
                        ? 'true'
                        : undefined
                    }
                    data-is-first-right-pinned-column={
                      cell.column.getIsPinned() === 'right' &&
                      cell.column.getIsFirstColumn('right')
                        ? 'true'
                        : undefined
                    }
                    style={getCommonPinningStyles(cell.column)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footerIncludesData ? (
            <tfoot className="group/footer">
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id} className="group/row">
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          ) : null}
        </table>
      </div>
      {paginated && pageSize >= 2 ? (
        <div className="typ-ui !mt-4 flex items-center justify-end space-x-4 overflow-x-auto text-neutral-100">
          <span>
            {t('pagination.summary', {
              current:
                fromItemIndex && toItemIndex
                  ? `${fromItemIndex}-${toItemIndex}`
                  : '0',
              length: table.getFilteredRowModel().rows.length,
            })}
          </span>
          <Pagination
            pagesCount={table.getPageCount()}
            onNextClick={table.nextPage}
            onPreviousClick={table.previousPage}
            currentPage={pageIndex + 1}
          >
            {Array.from({ length: table.getPageCount() }).map((_, index) => (
              <button onClick={() => table.setPageIndex(index)} key={index}>
                {index + 1}
              </button>
            ))}
          </Pagination>
        </div>
      ) : null}
    </>
  );
}

export default Table;
